import React, { FormEventHandler, useCallback, useMemo, useState } from 'react'
import { Tabs } from 'src/components/Reusable/Tabs/Tabs'
import { ModalForm, ModalFormId } from 'src/components/ModalForms'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { MultiSelectForm } from 'src/components/Reusable/Forms/MultiSelectForm'
import { getPluralization } from 'src/Utils/globalHelpers'
import { AttachmentPromiseClient } from '@trustero/trustero-api-web/lib/attachment/attachment_grpc_web_pb'
import { useGrpcRevalidateByMethod } from 'src/components'
import { SelectControlsRowItem } from 'src/components/Reusable/Forms/MultiSelectFromComponents/SelectControlsRowItem'
import { useLocation } from 'react-router'
import { EvidenceAbsoluteRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import { useControls } from 'src/components/async/model/control'
import { NoRecommendedTemplates } from 'src/components/ModalForms/Frameworks/AddFramework/NoRecommendedTemplates'
import { useInAudit } from 'src/context/AuditContext'
import { showInfoToast } from 'src/Utils/helpers/toast'
import log from 'loglevel'
import isFunction from 'lodash/isFunction'
import { GatekeeperFlag, IsGatekeeperEnabled } from 'src/context/Gatekeeper'
import { MessageType } from 'src/components/Reusable/Message/Message.constants'
import { Message } from 'src/components/Reusable/Message/Message'
import {
  useInvalidateEvidenceGroups,
  useLinkControlsToEvidence,
} from '../evidence.hooks'
import { SuggestedControlsTab } from './SuggestedControlsTab'

export enum LinkEvidenceToControlsModalType {
  CREATE = 'create',
  UPDATE = 'update',
}

export const LinkEvidenceToControlsModal = ({
  evidenceId,
  createdEvidenceIds,
  onHideFunc,
  onCreateFunc,
  existingControlIds,
  type = LinkEvidenceToControlsModalType.CREATE,
  formId = ModalFormId.LINK_EVIDENCE_TO_CONTROLS,
}: {
  evidenceId?: string
  createdEvidenceIds?: string[]
  onHideFunc?: () => void
  onCreateFunc?: () => void
  existingControlIds?: string[]
  type?: LinkEvidenceToControlsModalType
  formId?: ModalFormId
}): JSX.Element => {
  const location = useLocation()
  const { auditId } = useInAudit()
  const indexMutate = useInvalidateEvidenceGroups()
  const showMutate = useGrpcRevalidateByMethod()
  const isIndex = location.pathname.includes(EvidenceAbsoluteRoutes.INDEX)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [selectedControls, setSelectedControls] = useState<string[]>([])
  const show = useIsShowModal(formId)
  const onHide = () => {
    isFunction(onHideFunc) && onHideFunc()
    setSelectedControls([])
  }
  const hide = useHideModal({
    modalId: formId,
    onHide,
  })
  const { data, isLoading, error } = useControls()
  const linkControlsToEvidence = useLinkControlsToEvidence()

  const controls = useMemo(() => {
    if (!data) return []
    return data.getItemsList().filter((control) => {
      return !existingControlIds?.includes(control.getId())
    })
  }, [data, existingControlIds])

  const evidenceIds = useMemo(
    () => createdEvidenceIds ?? [],
    [createdEvidenceIds],
  )
  const showTabs = useMemo(() => {
    const hasControlEvidenceMappings = IsGatekeeperEnabled(
      GatekeeperFlag.CONTROL_EVIDENCE_MAPPINGS,
    )
    return evidenceIds.length > 0 && hasControlEvidenceMappings
  }, [evidenceIds])

  const onHideHandler = useCallback(() => {
    hide()
  }, [hide])

  const onSubmit: FormEventHandler = useCallback(
    async (e: React.MouseEvent<HTMLFormElement>) => {
      try {
        e.preventDefault()
        setIsSubmitting(true)
        if (type === LinkEvidenceToControlsModalType.UPDATE) {
          await linkControlsToEvidence(evidenceId ?? '', selectedControls)
          await showMutate(
            AttachmentPromiseClient.prototype.getEvidenceControls,
          )
        } else if (evidenceIds.length > 0 && selectedControls.length > 0) {
          await Promise.all(
            evidenceIds.map((id) =>
              linkControlsToEvidence(id, selectedControls),
            ),
          )
        }
      } catch (error) {
        log.error('Error linking evidence to Controls', error)
        showInfoToast('Error linking evidence to controls')
      } finally {
        if (isIndex) {
          indexMutate()
        }
        onCreateFunc?.()
        setIsSubmitting(false)
        onHideHandler()
      }
    },
    [
      evidenceIds,
      selectedControls,
      showMutate,
      onHideHandler,
      isIndex,
      linkControlsToEvidence,
      type,
      evidenceId,
      onCreateFunc,
      indexMutate,
    ],
  )

  const multiSelectFormProps = useMemo(
    () => ({
      formId,
      loading: isLoading,
      gridTemplateColumnStyling:
        'min-content minmax(min-content, auto) minmax(min-content, auto) minmax(min-content, 1fr)',
      gridItems: controls.length ? (
        <>
          {controls.map((control) => (
            <SelectControlsRowItem
              key={control.getId()}
              control={control}
              setSelectedControlIds={setSelectedControls}
              selectedControlIds={selectedControls}
              disabled={isSubmitting}
            />
          ))}
        </>
      ) : (
        <NoRecommendedTemplates templateType={'controls'} />
      ),
      numberSelectedText: `${selectedControls.length} ${getPluralization(
        'control',
        selectedControls.length,
      )} selected`,
      onSubmit,
    }),
    [isLoading, isSubmitting, controls, selectedControls, onSubmit, formId],
  )

  const showWarning = !!auditId && isIndex && selectedControls.length === 0

  if (!data) {
    if (error) {
      log.error(
        'Error fetching controls in link evidence to controls modal',
        error,
      )
    }
    return <></>
  }

  return (
    <ModalForm
      show={show}
      hide={onHideHandler}
      formId={formId}
      size="xl"
      title="Link Controls"
      description="The evidence will be linked to each of these controls"
      submitText={`Select ${selectedControls.length} ${getPluralization(
        'control',
        selectedControls.length,
      )}`}
      showOverflow
      disableSubmitButton={isSubmitting}
      busy={isSubmitting}
    >
      {showWarning && (
        <Message
          messageType={MessageType.WARNING}
          messageText={`Warning: this evidence won't be visible here. Link to a control in audit to make it visible. Evidence is only shown in an audit if a control in that audit is using it.`}
        />
      )}
      {showTabs ? (
        <Tabs
          tabs={[
            {
              title: 'All Controls',
              body: <MultiSelectForm {...multiSelectFormProps} />,
            },
            {
              title: 'Suggested Controls',
              body: (
                <SuggestedControlsTab
                  evidenceIds={evidenceIds}
                  selectedControls={selectedControls}
                  setSelectedControls={setSelectedControls}
                  isSubmitting={isSubmitting}
                  existingControlIds={existingControlIds}
                />
              ),
            },
          ]}
        />
      ) : (
        <MultiSelectForm {...multiSelectFormProps} />
      )}
    </ModalForm>
  )
}
